import { TGetAllAvailablePrizesResponse } from '@daymade/api-contracts';
import PrizeCategoryFilterSelector, {
  IPrizeCategoryFilterSelector,
} from 'components/landing/PrizeCategoryFilterSelector';
import LoadingOverlay from 'components/layout/LoadingOverlay';
import MainLayout from 'components/layout/MainLayout';
import PaddedMaxWidthContainer from 'components/layout/PaddedMaxWidthContainer';
import PageTitle from 'components/PageTitle';
import PlainLoadingSpinner from 'components/PlainLoadingSpinner';
import AllPrizesByCategory from 'components/prizes/all-prizes-page/AllPrizesByCategory';
import AllPrizesByTier from 'components/prizes/all-prizes-page/AllPrizesByTier';
import FilteredPrizeList, {
  renderGroupingHeader,
} from 'components/prizes/all-prizes-page/FilteredPrizeList';
import { GroupingDropdown } from 'components/prizes/all-prizes-page/GroupingDropdown';
import { getPrizePageDetails } from 'components/prizes/prize-page/PrizesPageDetails';
import PrizeCardWithTitleBelow from 'components/prizes/PrizeCardWithTitleBelow';
import PrizeList from 'components/prizes/PrizeList';
import { PrizePageNav } from 'components/prizes/PrizePageNav';
import PrizeTierDropdownSelector from 'components/prizes/PrizeTierDropdownSelector/PrizeTierDropdownSelector';
import SubcategorySelector from 'components/prizes/SubcategorySelector/SubcategorySelector';
import Separator from 'components/Separator';
import Subheading from 'components/Subheading';
import SubmitPrizeSection from 'components/SubmitPrizeSection';
import ToggleButton from 'components/ToggleButton';
import IntroducingWishListModal from 'components/wish-list/IntroducingWishListModal';
import { MainDrawScheduleCode, MainDrawTypeCode } from 'constants/Draw';
import { SUGGEST_PRIZE_ID } from 'constants/FaqConstants';
import { MAIN_PRIZE_CATEGORIES } from 'constants/PrizeCategories';
import { MAIN_DRAW_PRIZE_TIERS } from 'constants/PrizeTiers';
import { useDrawStore } from 'hooks/stores/useDrawStore';
import { useUserStore } from 'hooks/stores/useUserStore';
import {
  scrollPositionExists,
  useScrollRestoration,
} from 'hooks/useScrollRestoration';
import { observer } from 'mobx-react-lite';
import { NextSeo } from 'next-seo';
import { useRouter } from 'next/router';
import { ParsedUrlQuery } from 'querystring';
import { useEffect, useRef, useState } from 'react';
import { PrizeCategories, PrizeTiers } from 'types/Prize';
import { getAllPrizesPageTestValue } from 'utils/ab-test/abTests';
import {
  EecProduct,
  trackEecImpression,
  trackEecProductClick,
} from 'utils/analytics/analytics';
import { tryApi } from 'utils/api/common';
import { prizeApiClient } from 'utils/apiClient';
import { sleep } from 'utils/common';
import { ASSET_PREFIX, colors, Cta, SubscriberStatus } from 'utils/constants';

const EXPAND_LIMIT = 17;

const LegacyPrizesPage = observer(
  ({
    prizes,
    prizeCategories,
    prizeTiers,
    categoryFilter,
    initialPrizeTierFilter,
  }: LegacyProps) => {
    // Stores
    const { currentUser, subscriptionStatus, subscriptionStatusInfo } =
      useUserStore();
    const { prizesWonCount, treesPlantedCount } = useDrawStore();
    // Router settings
    const router = useRouter();
    const scrollPosition = scrollPositionExists(router);
    useScrollRestoration(router);
    // Page UI settings
    const [expanded, setExpanded] = useState(scrollPosition ? true : false);
    const [shownPrizes, setShownPrizes] = useState(prizes);

    // `null` indicates no filter
    const [prizeTierFilter, setPrizeTierFilter] = useState<string | null>(
      initialPrizeTierFilter ?? null,
    );

    // When a `categoryFilter` is supplied (i.e. we are on a specific prize page such as Trips & Stays), then `selectedSubcategory`
    // will also be considered when filtering the visible prizes. `null` indicates no filter.
    const [selectedSubcategory, setSelectedSubcategory] = useState<
      null | string
    >((router.query?.subcategory as string) ?? null);
    const getProductList = () => `prizes-page:${categoryFilter ?? 'none'}`;

    const [loadingMorePrizes, setLoadingMorePrizes] = useState(false);

    const [showIntroducingWishListModal, setShowIntroducingWishListModal] =
      useState(false);

    const trackProductClickAndGoToEnter = async () => {
      const subscriberStatus = subscriptionStatusInfo.subscriberStatus;
      const cta = subscriberStatus ? Cta[subscriberStatus] : Cta.NON_SUBSCRIBER;
      if (!cta) return;
      if (cta.url === '/enter') {
        // Track product click with GA EEC if it's a regular Enter Now button.
        await trackEecProductClick(getProductList(), EecProduct.LOTTERY);
      }
      router.push(cta.url);
    };

    const {
      title,
      description,
      availabilityNotice,
      subcategories,
      metaTitle,
      metaDescription,
      bottomInfoAndFaqSection,
    } = getPrizePageDetails(
      categoryFilter ?? undefined,
      false,
      prizesWonCount,
      treesPlantedCount,
      trackProductClickAndGoToEnter,
      null,
      prizeCategories,
    );

    let shouldShowExpandButton = false;

    // Used if showing one long list of prizes with no sections (the old page style).
    const filteredPrizes =
      !prizes || !Array.isArray(prizes)
        ? []
        : prizes.reduce(
            (filteredPrizes, prize) => {
              // Use reduce instead of filter, so we can dynamically check the size of `filteredPrizes` in the body below.
              if (!expanded && filteredPrizes.length > EXPAND_LIMIT) {
                shouldShowExpandButton = true;
                return filteredPrizes;
              }

              if (subcategories) {
                let allowedSubcategory = subcategories;
                if (selectedSubcategory) {
                  // When a subcategory is selected, then that is the only allowed category.
                  allowedSubcategory = [selectedSubcategory];
                }
                let allowed = false;
                for (let i = 0; i < allowedSubcategory.length; i++) {
                  const category = allowedSubcategory[i];
                  if (prize?.categories?.includes(category)) {
                    allowed = true;
                    break;
                  }
                }
                if (!allowed) return filteredPrizes;
              }

              if (prizeTierFilter && prizeTierFilter !== prize.tierCode) {
                return filteredPrizes;
              }

              filteredPrizes.push(prize);

              return filteredPrizes;
            },
            [] as typeof prizes,
          );

    // These variables are passed to the PrizePageNav breadcrumb component (if a filter is applied).
    let prizePageNavPrizeCategoryName, prizePageNavPrizeCategoryHref;
    if (categoryFilter) {
      const categoryProps = MAIN_PRIZE_CATEGORIES[categoryFilter];
      if (categoryProps) {
        prizePageNavPrizeCategoryName = categoryProps.name;
        prizePageNavPrizeCategoryHref = categoryProps.href;
      }
    }

    useEffect(() => {
      const abortController = new AbortController();

      setSelectedSubcategory(null);
      trackEecImpression(getProductList(), EecProduct.LOTTERY);
      return () => {
        abortController.abort(); // This would abort the trackEecImpression operation
      };
    }, [categoryFilter]);

    const shouldShowWishListIntroductionModal = () => {
      // Only show to logged in users.
      if (!currentUser) return false;

      // Only show to a user once.
      if (localStorage.getItem('SEEN_WISH_LIST_INTRO') === 'true') return false;

      return true;
    };

    const toggleShowAllPrizes = async () => {
      setLoadingMorePrizes(true);
      await sleep(100); // Needed to re-render component and show loading spinner.
      setExpanded(!expanded);
      setLoadingMorePrizes(false);
    };

    // Expands visible prize list once we have nearly scrolled to See More button.
    const seeMoreButton = useRef<HTMLHeadingElement>(null);
    const handleScroll = () => {
      if (window.location.hash?.includes(SUGGEST_PRIZE_ID)) {
        // If we're trying to scroll to the suggest prize area using normal browser URL-fragment scrolling, then we
        // should skip loading more on scroll
        return;
      }
      if (
        !expanded &&
        seeMoreButton.current &&
        seeMoreButton.current?.getBoundingClientRect()?.y -
          (window.innerHeight +
            200) /* so we load slightly before we see the button */ <
          0
      ) {
        toggleShowAllPrizes();
      }
    };

    useEffect(() => {
      window.addEventListener('scroll', handleScroll);

      if (shouldShowWishListIntroductionModal()) {
        setShowIntroducingWishListModal(true);
      }

      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }, []);

    return (
      <MainLayout additionalClassName="prizesPage">
        <NextSeo
          title={metaTitle}
          description={metaDescription}
          openGraph={{
            title: metaTitle,
            description: metaDescription,
          }}
        />

        {showIntroducingWishListModal && (
          <IntroducingWishListModal
            onClose={() => {
              localStorage.setItem('SEEN_WISH_LIST_INTRO', 'true');
              setShowIntroducingWishListModal(false);
            }}
          />
        )}

        <PageTitle
          heroBackgroundImage={`${ASSET_PREFIX}/images/prizes/hero-background.svg`}
          style={{
            minHeight: 135,
          }}
          tagClassName={
            'w-full container !text-3xl text-left !font-heavy normal-case max-w-screen-lg lg:px-8'
          }
        >
          <PrizePageNav
            prizeCategoryName={prizePageNavPrizeCategoryName}
            prizeCategoryHref={prizePageNavPrizeCategoryHref}
            className="md:px-4 lg:px-2 mb-10"
          />

          <div
            className="md:p-4"
            style={{
              fontSize: '1.1em',
            }}
          >
            Prizes
          </div>
        </PageTitle>

        {prizeCategories && (
          <PrizeCategoryFilterSelector
            selectedFilterCode={categoryFilter ?? 'all'}
            filterOptions={['all', ...Object.keys(prizeCategories)]
              .reduce(
                (acc, code) => {
                  if (code in MAIN_PRIZE_CATEGORIES) {
                    acc.push({ code, props: MAIN_PRIZE_CATEGORIES[code] });
                  }
                  return acc;
                },
                [] as IPrizeCategoryFilterSelector['filterOptions'],
              )
              .sort((a, b) => a.props.order - b.props.order)}
          />
        )}

        <PaddedMaxWidthContainer
          style={{ margin: 0, width: '100%', isolation: 'isolate' }}
        >
          <div className="p-6 mt-8 md:px-10 w-full">
            <div className="w-full md:mb-10">
              {
                <div className="w-full md:flex">
                  <div className="w-full md:w-2/3 md:pr-4">
                    <Subheading
                      style={{ textTransform: 'unset', textAlign: 'left' }}
                    >
                      {title}
                    </Subheading>
                    <p
                      className="!font-semibold"
                      style={{
                        fontSize: '1.3em',
                        fontFamily: "'Montserrat', sans-serif",
                      }}
                    >
                      {description}
                    </p>
                  </div>
                  {prizeTiers && (
                    <div className="flex justify-end mt-4 items-end p-1 w-full md:w-1/3 ">
                      <PrizeTierDropdownSelector
                        initialValue={prizeTierFilter}
                        options={prizeTiers
                          .map((code) => ({
                            code,
                            props: MAIN_DRAW_PRIZE_TIERS[code],
                          }))
                          .sort(
                            (a, b) =>
                              (a.props.order || 0) - (b.props.order || 0),
                          )}
                        onChange={setPrizeTierFilter}
                      />
                    </div>
                  )}
                </div>
              }

              {currentUser &&
                [SubscriberStatus.FREEMIUM, SubscriberStatus.LITE].includes(
                  subscriptionStatus as 'LITE' | 'FREEMIUM',
                ) && (
                  <p
                    style={{
                      fontStyle: 'italic',
                      textAlign: 'left',
                      fontSize: '1.1em',
                      padding: '1em 0',
                      width: '100%',
                    }}
                  >
                    Upgrade to DAYMADE Premium to unlock all of our prizes.
                  </p>
                )}
            </div>

            <div className="categoryAndFiltersSectionSeparator">
              <Separator style={{ width: '98%' }} />
            </div>

            {!!categoryFilter &&
              !!subcategories &&
              subcategories.length > 0 && (
                <div className="subcategorySelectorContainer">
                  <SubcategorySelector
                    selectedSubcategory={selectedSubcategory}
                    setSelectedSubcategory={setSelectedSubcategory}
                    subcategories={subcategories.filter(
                      (code) => !code.startsWith('other'),
                    )}
                  />
                </div>
              )}
          </div>

          <PrizeList pageSize={undefined} style={undefined}>
            {filteredPrizes.map((prize, key: number) => (
              <PrizeCardWithTitleBelow
                additionalCardClass={'h-64 md:h-80 lg:h-96'}
                titleStyle={{
                  fontFamily: "'Montserrat', sans-serif",
                  fontWeight: 700,
                }}
                subheadingStyle={{
                  fontFamily: "'Montserrat', sans-serif",
                  fontWeight: 400,
                }}
                key={key}
                prize={prize}
                withFavouriteButton={true}
                href={`/prizes/details/${prize.slug}`}
                showLockedPrizeBannerIfApplicable={true}
                style={undefined}
                displayGroup={undefined}
                onChangePrizeDisplayOrdering={undefined}
                customImageOpacity={undefined}
              />
            ))}
          </PrizeList>

          {filteredPrizes.length === 0 && (
            <PaddedMaxWidthContainer>
              <img
                src={`${ASSET_PREFIX}/images/no-prizes.png`}
                style={{ width: '120px', margin: '1.5em auto' }}
              />

              <p
                style={{
                  fontSize: '1.2em',
                  margin: '0 1em',
                  textAlign: 'center',
                }}
              >
                No prizes currently available in this category. Got any
                suggestions? Send us an email:{' '}
                <a href="mailto:hello@daymade.co.uk">hello@daymade.co.uk</a>.
                <br />
                <br />
                In the meantime, choose a different category at the top of the
                page to find more prizes.
              </p>
            </PaddedMaxWidthContainer>
          )}

          {shouldShowExpandButton && loadingMorePrizes ? (
            <PlainLoadingSpinner style={undefined} />
          ) : (
            shouldShowExpandButton && (
              <ToggleButton
                chevronReversed={expanded}
                toggleHandler={toggleShowAllPrizes}
                style={{
                  flexDirection: expanded ? 'column' : 'column-reverse',
                  margin: '0 auto',
                }}
                additionalClassName={undefined}
              >
                <h4
                  ref={seeMoreButton}
                  style={{ margin: '0.5em', fontSize: '1.3em' }}
                >
                  {expanded ? 'Show Less' : 'Show All'}
                </h4>
              </ToggleButton>
            )
          )}
        </PaddedMaxWidthContainer>

        <p
          style={{
            textAlign: 'center',
            margin: '2em',
            fontStyle: 'italic',
            fontWeight: 600,
            maxWidth: '1000px',
            fontFamily: '"Montserrat", sans-serif',
          }}
        >
          {availabilityNotice}
        </p>

        <SubmitPrizeSection
          prizeCategory="Weekly Draw"
          descriptionText="Have something you'd want the chance to experience? A place you've always wanted to go? Let us know!"
          suggestAPrizeId={SUGGEST_PRIZE_ID}
        />

        {bottomInfoAndFaqSection && (
          <div style={{ width: '100%', background: colors.footerBackground }}>
            <div
              className="flexCentered bottomInfoSection"
              style={{
                width: 'auto',
                maxWidth: '1000px',
                margin: '4em auto 2em',
                padding: '0 2em',
                alignItems: 'flex-start',
              }}
            >
              {bottomInfoAndFaqSection}
            </div>
          </div>
        )}

        <style jsx>{`
          .prizeTierSelectorContainer {
            grid-column: 2 / 3;
            grid-row: 1 / 2;

            display: flex;
            flex-wrap: wrap;
            flex-grow: 1;
            align-items: center;
            padding-left: 1em;
          }

          .categoryAndFiltersSectionSeparator {
            grid-column: 1 / 3;
            grid-row: 2 / 3;
          }

          .subcategorySelectorContainer {
            grid-column: 1 / 3;
            grid-row: 3 / 4;

            padding-top: 1em;
          }

          @media (max-width: 700px) {
            .categoryAndFiltersSection {
              padding: 2em 1em;
            }

            .subcategorySelectorContainer {
              grid-row: 2 / 3;

              padding-top: 0;
              padding-bottom: 1em;
            }

            .categoryAndFiltersSectionSeparator {
              grid-row: 3 / 4;
            }

            .prizeTierSelectorContainer {
              grid-column: 1 / 3;
              grid-row: 4 / 5;

              display: flex;
              justify-content: center;
              padding-top: 1em;
              padding-left: 0;
            }
          }

          .enjoyLaterBadgeContainer {
            position: absolute;
            width: 100%;
            max-width: 1000px;
            top: 100px;
            text-align: right;
          }

          .enjoyLaterBadge {
            display: inline;
            width: 40%;
            max-width: 192px;
            height: auto;
          }

          :global(.trendingPrizesContainer) {
            width: 100%;
            display: flex;
            flex-wrap: wrap;
            grid-gap: 5px;
            padding-bottom: 0.5em;
          }

          :global(.trendingPrizesGrid) {
            width: 100%;
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 5px;
            padding-bottom: 0.5em;
          }

          @media (min-width: 800px) {
            :global(.trendingPrizesGrid) {
              grid-template-columns: 1fr 1fr 1fr 1fr;
            }
          }

          :global(.footer) {
            margin-top: 0 !important;
          }

          :global(.bottomInfoSection .questionHeading) {
            font-size: 1em;
            letter-spacing: normal;
            text-transform: none;
            margin: 1em 0;
          }
        `}</style>
      </MainLayout>
    );
  },
);

/** The component that decides whether to show the legacy prize page or the AB test prize page.
 */
const PrizePageSwitchboard = () => {
  const router = useRouter();
  const tier = router.query.tier;
  const category = router.query?.filter;
  const searchTerm = router.query.term;

  const [legacyProps, setLegacyProps] = useState<LegacyProps>();
  const [loading, setLoading] = useState(false);
  const [categorySecondaryFilter, setCategorySecondaryFilter] = useState<
    string | undefined
  >(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const urlFragment = new URLSearchParams(
      window.location.hash.replace(/^#/g, ''),
    );
    if (tier) {
      return urlFragment.get('category') || undefined;
    }
  });
  const [tierSecondaryFilter, setTierSecondaryFilter] = useState<
    string | undefined
  >(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const urlFragment = new URLSearchParams(
      window.location.hash.replace(/^#/g, ''),
    );
    if (category) {
      return urlFragment.get('tier') || undefined;
    }
    return undefined;
  });

  const userStore = useUserStore();

  const abTestValue = getAllPrizesPageTestValue();

  useEffect(() => {
    if (
      router?.isReady &&
      abTestValue !== 'VARIANT1' &&
      abTestValue !== 'VARIANT2'
    ) {
      setLoading(true);
      (async () => {
        try {
          const response = await getLegacyPrizePageProps({
            query: router.query,
            params: router.query,
          });
          setLegacyProps(response.props);
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [
    abTestValue,
    router.isReady,
    router.query,
    router.query?.filter,
    router.query?.tier,
  ]);

  // If we're dealing with the AB test...
  if (abTestValue === 'VARIANT1' || abTestValue === 'VARIANT2') {
    // ...if we don't currently have any filters applied, send the user to either all prizes by tier or category
    if (!tier && !category && !searchTerm) {
      if (abTestValue === 'VARIANT1') {
        return <AllPrizesByTier />;
      } else {
        return <AllPrizesByCategory />;
      }
    }

    const tierForMatches =
      userStore.subscriptionStatusInfo.subscriberStatus || 'PREMIUM';

    const setSecondaryFilter = (newFilterValue: string | undefined) => {
      if (tier) {
        router.replace(newFilterValue ? '#category=' + newFilterValue : '#');
        setCategorySecondaryFilter(newFilterValue);
      } else if (category) {
        router.replace(newFilterValue ? '#tier=' + newFilterValue : '#');
        setTierSecondaryFilter(newFilterValue);
      }
    };

    return (
      <FilteredPrizeList
        showPrizeTierBadges={
          (Array.isArray(tier) ? tier[0] : tier) == undefined
        }
        headerComponentColor="white"
        headerComponent={
          <>
            {renderGroupingHeader(
              Array.isArray(category) ? category[0] : category,
              Array.isArray(tier) ? tier[0] : tier,
              tierForMatches,
              false,
            )}
            <div style={{ marginTop: 16 }}>
              <GroupingDropdown
                filterType={
                  (Array.isArray(category) ? category[0] : category)
                    ? 'tier'
                    : 'category'
                }
                selectedFilter={tierSecondaryFilter || categorySecondaryFilter}
                setSelectedFilter={setSecondaryFilter}
              />
            </div>
          </>
        }
        category={
          categorySecondaryFilter ||
          (Array.isArray(category) ? category[0] : category)
        }
        tier={tierSecondaryFilter || (Array.isArray(tier) ? tier[0] : tier)}
        term={Array.isArray(searchTerm) ? searchTerm[0] : searchTerm}
      />
    );
  }

  if (legacyProps) {
    return (
      <>
        {loading && <LoadingOverlay />}
        <LegacyPrizesPage {...legacyProps} />
      </>
    );
  }

  return (
    <MainLayout additionalClassName="prizesPage">
      <LoadingOverlay />
    </MainLayout>
  );
};

interface LegacyProps {
  prizes?: TGetAllAvailablePrizesResponse;
  prizeCategories?: PrizeCategories;
  prizeTiers?: PrizeTiers;
  categoryFilter?: string | null;
  initialPrizeTierFilter?: string | null;
}

const getLegacyPrizePageProps = async ({
  query,
  params,
}: {
  query: ParsedUrlQuery;
  params: ParsedUrlQuery;
}): Promise<{ props: LegacyProps | undefined }> => {
  let prizes: TGetAllAvailablePrizesResponse = [];
  const prizesResp = await tryApi(
    prizeApiClient.public.getAllAvailablePrizesV2({
      query: {
        drawTypeCode: MainDrawTypeCode,
        drawScheduleCode: MainDrawScheduleCode.TUESDAY,
      },
    }),
  );

  switch (prizesResp.status) {
    case 200:
      prizes = prizesResp.body.filter(
        ({ tierCode }) => tierCode in MAIN_DRAW_PRIZE_TIERS,
      );
      break;
    default:
      console.error(`Error fetching prizes: ${prizesResp.error}`);
      return { props: undefined };
  }

  let prizeCategories: PrizeCategories = {};
  const categoriesResp = await tryApi(prizeApiClient.public.getCategories());

  switch (categoriesResp.status) {
    case 200:
      prizeCategories = categoriesResp.body;
      break;
    case 404:
      console.error(
        `Error fetching prize categories: ${categoriesResp.body.message}`,
      );
      break;
  }

  let prizeTiers: PrizeTiers = [];
  const tiersResp = await tryApi(
    prizeApiClient.public.getAvailablePrizeTiersV2({
      query: {
        drawTypeCode: MainDrawTypeCode,
        drawScheduleCode: MainDrawScheduleCode.TUESDAY,
      },
    }),
  );

  switch (tiersResp.status) {
    case 200:
      prizeTiers = tiersResp.body.filter(
        (tierCode) => tierCode in MAIN_DRAW_PRIZE_TIERS,
      );
      break;
    case 404:
      console.error(`Error fetching prize tiers: ${tiersResp.body.message}`);
      break;
  }

  return {
    props: {
      prizes,
      prizeCategories,
      prizeTiers,
      categoryFilter: (params?.filter as string) ?? null,
      initialPrizeTierFilter: (query.tier as string) ?? null,
    },
  };
};

export default PrizePageSwitchboard;
